import { useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'

// Note: Badge is still used for displaying selected KPAs

export interface KPAOption {
  id: string | number
  name: string
  display_name?: string
  weight_percentage?: number
}

interface KPASelectorProps {
  selectedKPAIds: string[]
  onAdd: (kpaId: string) => void
  onRemove: (kpaId: string) => void
  label?: string
  disabled?: boolean
  kpas: KPAOption[]  // KPAs from user's objectives
}

export function KPASelector({
  selectedKPAIds,
  onAdd,
  onRemove,
  label = 'Linked KPAs',
  disabled = false,
  kpas
}: KPASelectorProps) {
  const [selectedKPAId, setSelectedKPAId] = useState<string>('')

  // Auto-add when selection changes
  const handleSelectionChange = (kpaId: string) => {
    if (kpaId && !selectedKPAIds.includes(kpaId)) {
      onAdd(kpaId)
      setSelectedKPAId('') // Clear selection after adding
    }
  }

  // Filter out already selected KPAs
  const availableKPAs = kpas.filter(kpa => !selectedKPAIds.includes(String(kpa.id)))
  const selectedKPAs = kpas.filter(kpa => selectedKPAIds.includes(String(kpa.id)))

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      
      {/* Selected KPAs */}
      {selectedKPAs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedKPAs.map(kpa => (
            <Badge 
              key={kpa.id} 
              variant="outline" 
              className="flex items-center gap-1 py-1.5 px-3 bg-emerald-50 border-emerald-200 text-emerald-900"
            >
              <span className="text-sm font-medium truncate max-w-[200px]">
                {kpa.display_name || kpa.name}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemove(String(kpa.id))}
                  className="ml-1 text-emerald-600 hover:text-red-600 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Add new KPA */}
      {!disabled && availableKPAs.length > 0 && (
        <div className="flex gap-2">
          <Select value={selectedKPAId} onValueChange={handleSelectionChange}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select KPA to link..." />
            </SelectTrigger>
            <SelectContent>
              {availableKPAs.map(kpa => (
                <SelectItem key={kpa.id} value={String(kpa.id)}>
                  <span className="truncate">{kpa.display_name || kpa.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {availableKPAs.length === 0 && selectedKPAs.length === 0 && (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-md p-3 border border-gray-200">
          <p className="font-medium mb-1">📊 KPA Linking</p>
          <p className="text-xs">
            No KPAs found. KPAs will appear here once you have objectives in your performance plan.
          </p>
        </div>
      )}
    </div>
  )
}
